/* eslint-disable complexity */

import { useContext, useMemo } from 'react';

import { Breakpoint, Countertop, nearestSixteenth, Panel, PanelCorners, PanelItemGroup, ReferenceMap, Size, Vertex3 } from '@rta/rta-blueprint-library';

import { AppContext } from '../app/app-context';

export type UsePanel = [ReferenceMap, {
	// panel: Panel;
	countertop: Countertop;
	// _id: string;
	breakpoints: Breakpoint[];
	panelItemGroups: PanelItemGroup[];
	sidePoints: PanelCorners;
	size: {
		physical: Size;
		visible: Size;
		outside: Size;
	};
	// _overhangs: {
	// 	left: number;
	// 	right: number;
	// 	front: number;
	// 	back: number;
	// };
	connections: {
		left: Panel;
		right: Panel;
	};
	isCountertop: boolean;
	// _countertopThickness: number;
	_offsets: {
		origin: Vertex3;
		surround: Vertex3;
		countertop: Vertex3;
	};
	// _materialThickness: number;
	maxHeightAbovePanel: number;
	// _referenceMap: ReferenceMap;
}];

/* eslint-disable max-statements */
export type UsePanelParams = {
	panelId: string;
	isCountertop?: boolean;
};

export const usePanel = (params: UsePanelParams): UsePanel => {
	// const {
	// 	arrayIndex: panelIndex,
	// 	refIsland: island,
	// 	refPanel: panel
	// } = useKitchenReference(panelKey);
	const {
		controller: { kitchen, options }
	} = useContext(AppContext);
	const { panelId, isCountertop: isCountertopOverride = false } = params;

	// const { panel, island } = useKitchenReference(panelId);
	// const sidePoints = useMemo(() => panel.getSidePoints({ includeCountertop: isCountertop || options.showCountertops }), [panel, options.showCountertops]);

	const referenceMap = useMemo<ReferenceMap>(() => {
		// console.log('usePanel:useMemo:getReferenceMap', panelId);
		return kitchen.getReferenceMap(panelId);
	}, [panelId]);

	const { island, panel } = referenceMap;
	const isCountertop = isCountertopOverride || panelId === null;

	const sidePoints = panel.getSidePoints({ includeCountertop: isCountertop || options.showCountertops, translate: true, rotate: true });

	// const island = kitchen.design.islands.findByPanelId(panelId);
	// const panel = island.panels.findById(panelId);
	// const countertops = kitchen.design.countertops.filter((c) => c.islandIds.includes(panelId)) || [];
	let countertops = null;
	try {
		countertops = kitchen.design.countertops.filterByIslandId(island.id);
	} catch (e) {
		console.log('usePanel:useMemo:countertops', kitchen.design);
		console.error(e);
	}

	// Max height above countertop
	const maxHeightAbovePanel = isCountertop ? 0 : panel.panelItemGroups.getMaxDistanceAboveCountertop();

	// panel item groups
	const panelItemGroups = isCountertop
		? (panel.panelItemGroups?.children.filter((panelItemGroup) => panelItemGroup.hasCountertopAppliances) as PanelItemGroup[]) || null
		: panel.panelItemGroups.children ?? [];

	// Countertop overhangs
	const leftPanel = panel.getLeftPanel();
	const rightPanel = panel.getRightPanel();

	const leftCountertopOverhang = leftPanel.countertopOverhang;
	const rightCountertopOverhang = rightPanel.countertopOverhang;
	const frontCountertopOverhang = panel.countertopOverhang;
	const backCountertopOverhang = panel.getBackPanels()[0]?.countertopOverhang || 0;

	// const leftCountertopOverhang = island.countertop.overhangs[panelIndex === RIGHT_INDEX ? FRONT_INDEX : panelIndex + 1];
	// const rightCountertopOverhang = island.countertop.overhangs[panelIndex === FRONT_INDEX ? RIGHT_INDEX : panelIndex - 1];
	// const frontCountertopOverhang = island.countertop.overhangs[FRONT_INDEX];
	// const backCountertopOverhang = island.countertop.overhangs[BACK_INDEX];
	const totalLeftRightOverhang = leftCountertopOverhang + rightCountertopOverhang;
	const totalFrontBackOverhang = frontCountertopOverhang + backCountertopOverhang;

	// material
	const panelThickness = panel.thickness;
	const countertopThickness = island.getCountertop()?.material.thickness;
	// Derive visible width from side points — the actual geometric corner-to-corner distance.
	// This works correctly for any panel thickness configuration without manual math.
	const localSidePoints = panel.getSidePoints({ includeCountertop: false, translate: false, rotate: false });
	const visiblePanelWidth = panel.isSidePanel && localSidePoints
		? localSidePoints.corners[0].getDistance(localSidePoints.corners[1])
		: panel.size.width;
	// Derive countertop width from side points with includeCountertop: true so that the side
	// panel's own overhang (plus adjacent panel overhangs and 45-degree joint geometry) are all
	// factored in geometrically. This ensures outsideSize.width updates when any relevant overhang changes.
	const countertopSidePoints = panel.isSidePanel
		? panel.getSidePoints({ includeCountertop: true, translate: false, rotate: false })
		: null;
	// Corner island side panels: round the countertop width to nearest 1/16" because the
	// 45° joint geometry produces irrational values. Only the front panel's countertop
	// width is allowed to remain a non-rounded float.
	const rawCountertopPanelWidth = countertopSidePoints
		? countertopSidePoints.corners[0].getDistance(countertopSidePoints.corners[1])
		: null;
	const countertopPanelWidth = rawCountertopPanelWidth !== null && island.isCornerIsland
		? nearestSixteenth(rawCountertopPanelWidth)
		: rawCountertopPanelWidth;

	// island dimensions
	const islandSize: Size = {
		height: island.islandSize.height + countertopThickness,
		width: island.islandSize.width,
		depth: island.islandSize.depth
	};

	// physical panel dimensions
	const physicalSize: Size = {
		height: isCountertop ? islandSize.depth + totalFrontBackOverhang : panel.size.height,
		width: isCountertop ? islandSize.width + totalLeftRightOverhang : panel.size.width,
		depth: isCountertop ? countertopThickness : panelThickness
	};

	// visible panel dimensions
	const visibleSize: Size = {
		height: physicalSize.height,
		width: isCountertop ? physicalSize.width : visiblePanelWidth,
		depth: physicalSize.depth
	};

	// outside panel dimensions (includes overhangs)
	// For side panels: use the geometrically-derived countertop width (includeCountertop: true) so that the
	// side panel's own overhang is factored in. For front/back panels and countertop views, use the previous
	// formula (visiblePanelWidth + totalLeftRightOverhang) which remains correct.
	const outsideSize: Size = {
		height: physicalSize.height + (isCountertop ? 0 : countertopThickness),
		width: isCountertop
			? physicalSize.width
			: (countertopPanelWidth !== null ? countertopPanelWidth : visiblePanelWidth + totalLeftRightOverhang),
		depth: physicalSize.depth
	};

	// Origin shifts
	const originOffset = new Vertex3({
		dataIn: {
			// x: (panel.size.width / -2) - (isCountertop ? -leftCountertopOverhang : 0),
			// y: isCountertop ? (panel.size.depth / -2) - frontCountertopOverhang : (panel.size.height / -2),
			// z: panel.size.depth / -2
			x: (isCountertop ? (islandSize.width / -2) : 0),
			y: (isCountertop ? (islandSize.depth / -2) : island.islandSize.height / -2),
			z: (isCountertop ? islandSize.height : panel.size.depth) / -2
		}
	});

	// Origin Shift surround — for side panels, shift x by front panel thickness
	const frontPanelDepth = island.frontPanel?.size.depth ?? panelThickness;
	const surroundOffset = new Vertex3({
		dataIn: {
			x: originOffset.x - (panel.isSidePanel ? frontPanelDepth : 0),
			y: originOffset.y,
			z: originOffset.z
		}
	});

	// Origin Shift Countertop
	const countertopOffset = new Vertex3({
		dataIn: {
			x: surroundOffset.x - leftCountertopOverhang,
			y: surroundOffset.y - (isCountertop ? backCountertopOverhang : countertopThickness),
			z: isCountertop ? surroundOffset.z : surroundOffset.z
		}
	});

	// surround
	// const materialAsset = surroundAssetMap[kitchen.material.surround] ?? surroundAssetMap.unknown;
	// const xSurround = xSurroundShift + (options.corner.show ? options.corner.width - options.corner.thickness : 0);
	// const surroundWidth = visiblePanelWidth - (options.corner.show ? ((options.corner.width - options.corner.thickness) * 2) : 0);
	// const materialScale = DESIGNER_TEXTURE_DIMENSION * materialAsset.scale;

	const hookData = {
		/* get the first countertop */
		countertop: countertops[0] as Countertop,
		/* breakpoints */
		breakpoints: isCountertop ? (countertops[0] as Countertop)?.breakpoints.children ?? [] : panel.breakpoints.children,
		/* filtered groups based on isCountertop */
		panelItemGroups,
		/* side points */
		sidePoints,
		/* physical, visible, and outside sizes */
		size: {
			physical: physicalSize,
			visible: visibleSize,
			outside: outsideSize
		},
		/* connections, return left and right panels */
		connections: {
			left: leftPanel,
			right: rightPanel
		},
		// overhangs: {
		// 	left: leftCountertopOverhang,
		// 	right: rightCountertopOverhang,
		// 	front: frontCountertopOverhang,
		// 	back: backCountertopOverhang
		// },
		isCountertop,
		// _countertopThickness: countertopThickness,
		_offsets: {
			origin: originOffset,
			surround: surroundOffset,
			countertop: countertopOffset
		},
		// _materialThickness: materialThickness,
		maxHeightAbovePanel
	};

	// console.log('uPanel-Hook', uPanel);
	return [referenceMap, hookData];
};
